<template>
  <span class="ri">
    <button
      type="button"
      :class="['ri-trigger', `ri-trigger--${variant}`]"
      :title="hint"
      @click="openDialog"
    >
      <span class="ri-trigger__icon" aria-hidden="true">!</span>
      <span class="ri-trigger__text">{{ label }}</span>
    </button>

    <teleport to="body">
      <transition name="ri-fade">
        <div v-if="open" class="ri-backdrop" @mousedown.self="close">
          <form class="ri-dialog" role="dialog" aria-modal="true" :aria-labelledby="titleId" @submit.prevent="submit">
            <h2 :id="titleId" class="ri-dialog__title">Информация неверна</h2>
            <p class="ri-dialog__lead">
              Напишите своими словами, что не сходится с жизнью: сумма, срок, рейс, статус или текст письма.
              Система сама приложит, что было на экране.
            </p>
            <p v-if="tripNumber" class="ri-dialog__object">Рейс <b>{{ tripNumber }}</b></p>
            <label class="ri-label" :for="fieldId">Что не так</label>
            <textarea
              :id="fieldId"
              ref="field"
              v-model="comment"
              class="ri-input"
              rows="4"
              maxlength="4000"
              placeholder="Например: это «Красное & Белое», сумма должна быть 50 %, а не 25 %"
              :disabled="sending || Boolean(saved)"
            ></textarea>
            <p v-if="error" class="ri-error" role="alert">{{ error }}</p>
            <p v-if="saved" class="ri-saved" role="status">{{ saved }}</p>
            <div class="ri-actions">
              <button type="button" class="ri-btn ri-btn--ghost" :disabled="sending" @click="close">
                {{ saved ? 'Закрыть' : 'Отмена' }}
              </button>
              <button v-if="!saved" type="submit" class="ri-btn" :disabled="sending">
                {{ sending ? 'Записываем…' : 'Сообщить' }}
              </button>
            </div>
          </form>
        </div>
      </transition>
    </teleport>
  </span>
</template>

<script setup>
import { nextTick, ref } from 'vue'
import { useRoute } from 'vue-router'
import { createFeedback } from '@/backend/feedbackApi'
import {
  KIND_INACCURACY,
  buildFeedbackPayload,
  errorText,
  savedText,
  validateFeedback,
} from '@/components/feedback/feedbackLogic.mjs'

const props = defineProps({
  section: { type: String, default: '' },
  objectType: { type: String, default: '' },
  objectId: { type: [String, Number], default: '' },
  tripNumber: { type: [String, Number], default: '' },
  context: { type: [Object, Array, String, Number], default: null },
  label: { type: String, default: 'Информация неверна' },
  hint: { type: String, default: 'Сообщить, что данные на экране не сходятся с жизнью' },
  variant: { type: String, default: 'link' },   // link — строка таблицы, ghost — кнопка-пилюля
})

const uid = `ri-${Math.random().toString(36).slice(2, 10)}`
const titleId = `${uid}-title`
const fieldId = `${uid}-field`
const route = useRoute()
const open = ref(false)
const comment = ref('')
const sending = ref(false)
const error = ref('')
const saved = ref('')
const field = ref(null)

async function openDialog() {
  open.value = true
  comment.value = ''
  error.value = ''
  saved.value = ''
  await nextTick()
  field.value?.focus()
}

function close() {
  if (sending.value) return
  open.value = false
}

async function submit() {
  const check = validateFeedback(comment.value)
  if (!check.ok) {
    error.value = check.error
    return
  }
  sending.value = true
  error.value = ''
  try {
    const item = await createFeedback(buildFeedbackPayload({
      kind: KIND_INACCURACY,
      comment: check.comment,
      page: route?.fullPath || (typeof location !== 'undefined' ? location.pathname : ''),
      section: props.section,
      objectType: props.objectType,
      objectId: props.objectId,
      tripNumber: props.tripNumber,
      context: props.context,
    }))
    saved.value = savedText(item)
  } catch (e) {
    error.value = errorText(e)
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.ri { display: inline-flex; }
.ri-trigger {
  display: inline-flex; align-items: center; gap: 6px; border: 0; background: none; cursor: pointer;
  font: inherit; color: #8a6d1f; padding: 2px 0; font-size: 12.5px; font-weight: 600;
  transition: color .15s ease;
}
.ri-trigger:hover { color: #1f2430; }
.ri-trigger__icon {
  width: 16px; height: 16px; border-radius: 50%; display: inline-grid; place-items: center;
  background: #fff3d6; color: #92400e; font-size: 11px; font-weight: 800;
}
.ri-trigger--ghost {
  border: 1px solid #e7e5df; background: #f7f5ef; color: #1f2430; border-radius: 999px; padding: 9px 16px;
  font-size: 14px; font-weight: 700;
}
.ri-trigger--ghost:hover { border-color: #f2b705; }

.ri-backdrop {
  position: fixed; inset: 0; z-index: 2100; background: rgba(31, 36, 48, .42);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.ri-dialog {
  width: min(520px, 100%); background: #fffdf8; border: 1px solid #e7e5df; border-radius: 20px;
  padding: 22px; box-shadow: 0 24px 48px rgba(31, 36, 48, .22); display: flex; flex-direction: column; gap: 8px;
}
.ri-dialog__title { margin: 0; font-size: 19px; color: #1f2430; }
.ri-dialog__lead { margin: 0; font-size: 13.5px; color: #6b7280; line-height: 1.45; }
.ri-dialog__object { margin: 2px 0 0; font-size: 13.5px; color: #374151; }
.ri-label { margin-top: 6px; font-size: 13px; font-weight: 700; color: #374151; }
.ri-input {
  width: 100%; border: 1px solid #e7e5df; border-radius: 12px; padding: 10px 12px; font: inherit; resize: vertical;
  background: #fff;
}
.ri-input:focus { outline: 2px solid #fde68a; border-color: #f2b705; }
.ri-error { margin: 0; color: #dc2626; font-size: 13.5px; }
.ri-saved { margin: 0; color: #166534; background: #dcfce7; border-radius: 12px; padding: 8px 12px; font-size: 13.5px; }
.ri-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 6px; flex-wrap: wrap; }
.ri-btn {
  border: 0; border-radius: 999px; padding: 9px 18px; font: inherit; font-weight: 700; cursor: pointer;
  background: linear-gradient(135deg, #ffe08a, #f2b705); color: #1f2430;
}
.ri-btn--ghost { background: #f3f1ea; }
.ri-btn:disabled { opacity: .55; cursor: progress; }

.ri-fade-enter-active, .ri-fade-leave-active { transition: opacity .18s ease; }
.ri-fade-enter-active .ri-dialog, .ri-fade-leave-active .ri-dialog { transition: transform .18s ease; }
.ri-fade-enter-from, .ri-fade-leave-to { opacity: 0; }
.ri-fade-enter-from .ri-dialog, .ri-fade-leave-to .ri-dialog { transform: translateY(8px) scale(.98); }
@media (prefers-reduced-motion: reduce) {
  .ri-fade-enter-active, .ri-fade-leave-active, .ri-fade-enter-active .ri-dialog, .ri-fade-leave-active .ri-dialog {
    transition: none;
  }
}
</style>
